import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { RadioGroup, RadioGroupItem } from '../ui/RadioGroup';
import { Checkbox } from '../ui/Checkbox';
import { defaultQuestionnaireTemplate } from '../../lib/defaultTemplate';
import { ConditionalRulesEditor } from './ConditionalRulesEditor';
import { useQuestionnaire, DashboardSettings } from '../../lib/QuestionnaireContext';
import RichTextEditor from '../ui/RichTextEditor';
import EmailNotificationSettings from './EmailNotificationSettings';

interface QuestionOption {
  id: string;
  label: string;
  value: string;
  conditionalTrigger?: ConditionalRule[];
}

interface ConditionalRule {
  id: string;
  type: 'show' | 'hide' | 'require' | 'skip';
  targetQuestionId?: string;
  targetSectionId?: string;
  conditions: {
    questionId: string;
    operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'in' | 'not_in';
    value: any;
    logicalOperator?: 'AND' | 'OR';
  }[];
}

interface Question {
  id: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'radio' | 'checkbox' | 'number' | 'email' | 'url' | 'date' | 'file' | 'range' | 'boolean' | 'repeater' | 'custom';
  title: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  accept?: string; // For file input types
  min?: number; // For range and number input types
  max?: number; // For range and number input types
  options?: QuestionOption[];
  conditionalRules?: ConditionalRule[];
  metadata?: {
    category?: string;
    tags?: string[];
    version?: number;
    reusable?: boolean;
  };
}

interface Subsection {
  id: string;
  title: string;
  description?: string;
  order: number;
  questions: Question[];
  conditionalRules?: ConditionalRule[];
}

interface Section {
  id: string;
  title: string;
  description?: string;
  order: number;
  questions: Question[];
  subsections?: Subsection[];
  conditionalRules?: ConditionalRule[];
}

interface QuestionnaireTemplate {
  id?: string;
  name: string;
  title: string;
  description?: string;
  version?: number;
  isActive?: boolean;
  sections: Section[];
  globalConditionalRules?: ConditionalRule[];
  settings: {
    allowSaveProgress: boolean;
    allowBackNavigation: boolean;
    showProgressBar: boolean;
    requireAuthentication: boolean;
    maxSubmissions?: number;
    expirationDate?: Date;
    thankYouMessage?: string;
    redirectUrl?: string;
  };
  metadata?: {
    category?: string;
    tags?: string[];
    estimatedTimeMinutes?: number;
  };
  branding?: {
    primaryColor?: string;
    secondaryColor?: string;
    logo?: string;
    customCSS?: string;
  };
}

interface QuestionnaireTemplateBuilderProps {
  template?: QuestionnaireTemplate;
  onSave: (template: QuestionnaireTemplate) => void;
  onCancel: () => void;
}

interface DraggableSectionCardProps {
  section: Section;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (dragIndex: number, hoverIndex: number) => void;
}

interface DraggableQuestionCardProps {
  question: Question;
  index: number;
  sectionId: string;
  subsectionId?: string;
  questionTypes: { value: string; label: string; hasOptions: boolean }[];
  onEdit: () => void;
  onDelete: () => void;
  onMove: (dragIndex: number, hoverIndex: number) => void;
}

const DraggableSectionCard: React.FC<DraggableSectionCardProps> = ({
  section,
  index,
  onEdit,
  onDelete,
  onMove
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [draggedOver, setDraggedOver] = React.useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, type: 'section' }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOver(true);
  };

  const handleDragLeave = () => {
    setDraggedOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedOver(false);
    
    const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
    const { index: dragIndex, type } = dragData;
    
    // Only allow reordering sections
    if (type === 'section' && dragIndex !== index) {
      onMove(dragIndex, index);
    }
  };

  return (
    <Card 
      className={`border-l-4 border-l-blue-500 transition-all duration-200 ${
        isDragging ? 'opacity-50 transform scale-95' : ''
      } ${
        draggedOver ? 'border-blue-500 border-2' : ''
      }`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex items-center flex-1">
            <div className="mr-3 cursor-move text-gray-400 hover:text-gray-600">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2 4h2v2H2V4zm0 4h2v2H2V8zm0 4h2v2H2v-2zm4-8h2v2H6V4zm0 4h2v2H6V8zm0 4h2v2H6v-2zm4-8h2v2h-2V4zm0 4h2v2h-2V8zm0 4h2v2h-2v-2z"/>
              </svg>
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">{section.title}</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                {(() => {
                  const directQuestions = section.questions.length;
                  const subsectionCount = section.subsections?.length || 0;
                  const totalQuestionsInSubsections = section.subsections?.reduce((total, subsection) => 
                    total + subsection.questions.length, 0) || 0;
                  const totalQuestions = directQuestions + totalQuestionsInSubsections;
                  
                  if (subsectionCount === 0) {
                    return `${totalQuestions} question${totalQuestions !== 1 ? 's' : ''}`;
                  } else {
                    return `${subsectionCount} subsection${subsectionCount !== 1 ? 's' : ''}, ${totalQuestions} question${totalQuestions !== 1 ? 's' : ''}`;
                  }
                })()}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
            >
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              className="text-red-600"
            >
              Delete
            </Button>
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

const DraggableQuestionCard: React.FC<DraggableQuestionCardProps> = ({
  question,
  index,
  sectionId,
  subsectionId,
  questionTypes,
  onEdit,
  onDelete,
  onMove
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [draggedOver, setDraggedOver] = React.useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    e.stopPropagation(); // Prevent parent subsection drag from triggering
    setIsDragging(true);
    const dragData = { index, sectionId, subsectionId, question };
    console.log('🚀 QUESTION DRAG START:', {
      questionTitle: question.title,
      sectionId,
      subsectionId,
      index
    });
    e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOver(true);
  };

  const handleDragLeave = () => {
    setDraggedOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent parent subsection drop from triggering
    setDraggedOver(false);
    
    try {
      const rawData = e.dataTransfer.getData('text/plain');
      const dragData = JSON.parse(rawData);
      const { index: dragIndex, sectionId: dragSectionId, subsectionId: dragSubsectionId } = dragData;
      
      // Allow reordering within the same container (section + subsection combo)
      if (dragSectionId === sectionId && dragSubsectionId === subsectionId && dragIndex !== index) {
        onMove(dragIndex, index);
      }
      // Note: Cross-container drops are handled by the SubsectionCard component
    } catch (error) {
      console.error('Error in handleDrop:', error);
    }
  };

  return (
    <Card 
      className={`mb-4 transition-all duration-200 ${
        isDragging ? 'opacity-50 transform scale-95' : ''
      } ${
        draggedOver ? 'border-blue-500 border-2' : ''
      }`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center flex-1">
            <div className="mr-3 cursor-move text-gray-400 hover:text-gray-600">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2 4h2v2H2V4zm0 4h2v2H2V8zm0 4h2v2H2v-2zm4-8h2v2H6V4zm0 4h2v2H6V8zm0 4h2v2H6v-2zm4-8h2v2h-2V4zm0 4h2v2h-2V8zm0 4h2v2h-2v-2z"/>
              </svg>
            </div>
            <div className="flex-1">
              <h5 className="font-medium">{question.title}</h5>
              <p className="text-sm text-gray-600">
                Type: {questionTypes.find(t => t.value === question.type)?.label}
                {question.required && ' • Required'}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onEdit}
            >
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              className="text-red-600"
            >
              Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Enhanced Subsection Card component
interface SubsectionCardProps {
  subsection: Subsection;
  index: number;
  sectionId: string;
  onEdit: (subsection: Subsection) => void;
  onDelete: (subsectionId: string) => void;
  onMoveSubsection: (dragIndex: number, hoverIndex: number) => void;
  questionTypes: any[];
  moveQuestion: (sectionId: string, dragIndex: number, hoverIndex: number, subsectionId?: string) => void;
  setCurrentQuestion: (question: Question | null) => void;
  setCurrentQuestionSubsectionId: (subsectionId: string | null) => void;
  deleteQuestion: (sectionId: string, questionId: string, subsectionId?: string) => void;
  moveQuestionBetweenContainers: (
    draggedItem: { id: string; sectionId: string; subsectionId?: string; question: Question },
    targetSectionId: string,
    targetSubsectionId?: string
  ) => void;
}

const SubsectionCard: React.FC<SubsectionCardProps> = ({
  subsection,
  index,
  sectionId,
  onEdit,
  onDelete,
  onMoveSubsection,
  questionTypes,
  moveQuestion,
  setCurrentQuestion,
  setCurrentQuestionSubsectionId,
  deleteQuestion,
  moveQuestionBetweenContainers
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [draggedOver, setDraggedOver] = React.useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, sectionId, type: 'subsection' }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOver(true);
  };

  const handleDragLeave = () => {
    setDraggedOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedOver(false);
    
    try {
      const rawData = e.dataTransfer.getData('text/plain');
      console.log('📦 SUBSECTION DROP - Raw Data:', rawData);
      
      const dragData = JSON.parse(rawData);
      console.log('📦 SUBSECTION DROP - Parsed Data:', dragData);
      
      if (dragData.type === 'subsection') {
        console.log('🔄 SUBSECTION REORDER');
        // Handle subsection reordering
        const { index: dragIndex, sectionId: dragSectionId } = dragData;
        if (dragSectionId === sectionId && dragIndex !== index) {
          onMoveSubsection(dragIndex, index);
        }
      } else if (dragData.question) {
        console.log('🎯 QUESTION DROP TO SUBSECTION:', {
          from: `${dragData.sectionId}/${dragData.subsectionId}`,
          to: `${sectionId}/${subsection.id}`,
          questionTitle: dragData.question.title
        });
        
        // Handle question drop from another subsection/section
        const { sectionId: dragSectionId, subsectionId: dragSubsectionId, question } = dragData;
        
        // Don't allow dropping into the same subsection
        if (dragSectionId === sectionId && dragSubsectionId === subsection.id) {
          console.log('❌ DROP IGNORED - Same subsection');
          return;
        }
        
        console.log('✅ EXECUTING moveQuestionBetweenContainers');
        moveQuestionBetweenContainers(
          {
            id: question.id,
            sectionId: dragSectionId,
            subsectionId: dragSubsectionId,
            question: question
          },
          sectionId,
          subsection.id
        );
      } else {
        console.log('❓ UNKNOWN DRAG DATA TYPE:', dragData);
      }
    } catch (error) {
      console.error('❌ Error in SubsectionCard handleDrop:', error);
    }
  };

  return (
    <Card 
      className={`mb-4 border-l-4 border-l-black transition-all duration-200 ${
        isDragging ? 'opacity-50 transform scale-95' : ''
      } ${
        draggedOver ? 'border-blue-500 border-2 bg-blue-50' : ''
      }`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="cursor-move" title="Drag to reorder">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M8 7h8M8 12h8M8 17h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-purple-700">
                {subsection.title}
              </h4>
              <p className="text-xs text-gray-500">
                {subsection.description || 'No description'}
              </p>
              <p className="text-xs text-gray-400">
                {subsection.questions.length} question{subsection.questions.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(subsection)}
              className="text-blue-600"
            >
              Open
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(subsection.id)}
              className="text-red-600"
            >
              Delete
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {/* Questions within subsection */}
      <CardContent className="pt-0">
        <div className="pl-6 border-l-2 border-purple-200">
          {subsection.questions.map((question, qIndex) => (
            <DraggableQuestionCard
              key={question.id}
              question={question}
              index={qIndex}
              sectionId={sectionId}
              subsectionId={subsection.id}
              questionTypes={questionTypes}
              onEdit={() => {
                setCurrentQuestion(question);
                setCurrentQuestionSubsectionId(subsection.id);
              }}
              onDelete={() => deleteQuestion(sectionId, question.id, subsection.id)}
              onMove={(dragIndex: number, hoverIndex: number) => moveQuestion(sectionId, dragIndex, hoverIndex, subsection.id)}
            />
          ))}
          {subsection.questions.length === 0 && (
            <div className={`text-center py-4 text-gray-400 text-sm border border-dashed rounded-lg transition-colors ${
              draggedOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}>
              {draggedOver ? 'Drop question here' : 'No questions yet. Questions will appear here.'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};



const QuestionnaireTemplateBuilder: React.FC<QuestionnaireTemplateBuilderProps> = ({
  template,
  onSave,
  onCancel
}) => {
  const { dashboardSettings, loadDashboardSettings, updateDashboardSettings } = useQuestionnaire();
  
  const [currentTemplate, setCurrentTemplate] = useState<QuestionnaireTemplate>(
    template || {
      name: '',
      title: '',
      description: '',
      sections: [],
      settings: {
        allowSaveProgress: true,
        allowBackNavigation: true,
        showProgressBar: true,
        requireAuthentication: false
      },
      metadata: {
        tags: [],
        estimatedTimeMinutes: 10
      }
    }
  );

  const [loading, setLoading] = useState<boolean>(!template);

  // Load the active template when component mounts (if no template prop provided)
  useEffect(() => {
    const loadActiveTemplate = async () => {
      if (template) return; // Skip if template is provided via props
      
      try {
        setLoading(true);
        
        // Get Firebase auth token
        const auth = (await import('../../lib/firebase')).auth;
        const user = auth.currentUser;
        if (!user) {
          console.log('No authenticated user found');
          return;
        }
        
        const token = await user.getIdToken();
        
        const response = await fetch('https://api-reuvze3ckq-uc.a.run.app/api/questionnaire-templates/active', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const activeTemplate = await response.json();
          // If it's the default empty template from backend, use our migrated template instead
          if (activeTemplate.id === 'default' || !activeTemplate.sections || activeTemplate.sections.length === 0) {
            console.log('Using migrated current questionnaire as default template');
            setCurrentTemplate({
              ...defaultQuestionnaireTemplate,
              id: 'default'
            } as any);
          } else {
            setCurrentTemplate(activeTemplate);
          }
        } else {
          console.log('No active template found, using migrated questionnaire as default');
          setCurrentTemplate({
            ...defaultQuestionnaireTemplate,
            id: 'default'
          } as any);
        }
      } catch (error) {
        console.error('Error loading active template:', error);
        // Use default template on error
        setCurrentTemplate({
          ...defaultQuestionnaireTemplate,
          id: 'default'
        } as any);
      } finally {
        setLoading(false);
      }
    };

    loadActiveTemplate();
  }, [template]);

  const [currentSection, setCurrentSection] = useState<Section | null>(null);
  const [currentSubsection, setCurrentSubsection] = useState<Subsection | null>(null);
  const [isDragOperation, setIsDragOperation] = useState<boolean>(false);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [currentQuestionSubsectionId, setCurrentQuestionSubsectionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'sections' | 'settings' | 'email-notifications' | 'preview'>('basic');
  const [dashboardFormData, setDashboardFormData] = useState<DashboardSettings>({
    welcomeTitle: '',
    welcomeDescription: '',
    startButtonText: ''
  });
  const [dashboardSaving, setDashboardSaving] = useState(false);
  const [dashboardSaved, setDashboardSaved] = useState(false);

  // Load dashboard settings when component mounts
  useEffect(() => {
    loadDashboardSettings();
  }, [loadDashboardSettings]);

  // Update form data when dashboard settings change
  useEffect(() => {
    if (dashboardSettings) {
      setDashboardFormData(dashboardSettings);
    }
  }, [dashboardSettings]);



  // Migration utility: Move direct questions to subsections
  const migrateDirectQuestionsToSubsections = useCallback(() => {
    const updatedSections = currentTemplate.sections.map(section => {
      if (section.questions.length > 0 && (!section.subsections || section.subsections.length === 0)) {
        // Section has direct questions but no subsections - create a default subsection
        const defaultSubsection: Subsection = {
          id: `subsection_${Date.now()}_${section.id}`,
          title: section.title,
          description: section.description || '',
          order: 1,
          questions: [...section.questions]
        };

        return {
          ...section,
          questions: [], // Clear direct questions
          subsections: [defaultSubsection]
        };
      } else if (section.questions.length > 0 && section.subsections && section.subsections.length > 0) {
        // Section has both direct questions and subsections - move direct questions to first subsection
        const updatedSubsections = [...section.subsections];
        const firstSubsection = updatedSubsections[0];
        
        updatedSubsections[0] = {
          ...firstSubsection,
          questions: [...firstSubsection.questions, ...section.questions]
        };

        return {
          ...section,
          questions: [], // Clear direct questions
          subsections: updatedSubsections
        };
      }
      
      return section;
    });

    const migrationCallId = `MIGRATION_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log('🔄 Migration setCurrentTemplate called - Call ID:', migrationCallId);
    
    setCurrentTemplate(prev => {
      console.log('📥 Migration setCurrentTemplate executing - Call ID:', migrationCallId);
      return {
        ...prev,
        sections: updatedSections
      };
    });
  }, [currentTemplate]);

  // Auto-migrate any remaining direct questions to subsections
  useEffect(() => {
    // Don't auto-migrate during drag operations to avoid interference
    if (isDragOperation) {
      console.log('🚫 Auto-migration skipped - drag operation in progress');
      return;
    }
    
    const hasDirectQuestions = currentTemplate.sections.some(section => 
      section.questions && section.questions.length > 0
    );
    
    if (hasDirectQuestions) {
      console.log('🔄 Auto-migration triggered for direct questions');
      migrateDirectQuestionsToSubsections();
    }
  }, [currentTemplate.sections, migrateDirectQuestionsToSubsections, isDragOperation]);

  // Question types with their configurations
  const questionTypes = [
    { value: 'text', label: 'Short Text', hasOptions: false },
    { value: 'textarea', label: 'Long Text', hasOptions: false },
    { value: 'select', label: 'Dropdown', hasOptions: true },
    { value: 'multiselect', label: 'Multi-Select Dropdown', hasOptions: true },
    { value: 'radio', label: 'Radio Buttons', hasOptions: true },
    { value: 'checkbox', label: 'Checkboxes', hasOptions: true },
    { value: 'number', label: 'Number', hasOptions: false },
    { value: 'email', label: 'Email', hasOptions: false },
    { value: 'url', label: 'URL', hasOptions: false },
    { value: 'date', label: 'Date', hasOptions: false },
    { value: 'file', label: 'File Upload', hasOptions: false },
    { value: 'range', label: 'Range Slider', hasOptions: false },
    { value: 'boolean', label: 'Yes/No', hasOptions: false },
    { value: 'repeater', label: 'Repeater (Page Structure)', hasOptions: false }
  ];

  const addSection = () => {
    const sectionId = `section_${Date.now()}`;
    const defaultSubsection: Subsection = {
      id: `subsection_${Date.now()}`,
      title: 'New Section',
      description: undefined, // Make description truly optional
      order: 1,
      questions: []
    };

    const newSection: Section = {
      id: sectionId,
      title: 'New Section',
      description: undefined, // Make description truly optional
      order: currentTemplate.sections.length,
      questions: [],
      subsections: [defaultSubsection],
      conditionalRules: []
    };

    setCurrentTemplate(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    setCurrentSection(newSection);
  };

  const updateSection = (sectionId: string, updates: Partial<Section>) => {
    setCurrentTemplate(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId ? { ...section, ...updates } : section
      )
    }));

    if (currentSection?.id === sectionId) {
      setCurrentSection(prev => prev ? { ...prev, ...updates } : null);
    }
  };

  const deleteSection = (sectionId: string) => {
    setCurrentTemplate(prev => ({
      ...prev,
      sections: prev.sections.filter(section => section.id !== sectionId)
    }));

    if (currentSection?.id === sectionId) {
      setCurrentSection(null);
    }
  };

  // Update conditional rules for a section
  const updateSectionConditionalRules = (sectionId: string, conditionalRules: ConditionalRule[]) => {
    updateSection(sectionId, { conditionalRules });
  };
  
  // Get all available questions for conditional rules
  const getAllAvailableQuestions = () => {
    const questions: Array<{
      id: string;
      title: string;
      type: string;
      sectionId: string;
      sectionTitle: string;
      options?: QuestionOption[];
    }> = [];
    
    currentTemplate.sections.forEach(section => {
      // Add questions from subsections
      if (section.subsections && section.subsections.length > 0) {
        section.subsections.forEach(subsection => {
          subsection.questions.forEach(question => {
            questions.push({
              id: question.id,
              title: question.title,
              type: question.type,
              sectionId: section.id,
              sectionTitle: `${section.title} - ${subsection.title}`,
              options: question.options
            });
          });
        });
      }
      

    });
    
    return questions;
  };

  // Dashboard settings functions
  const handleDashboardInputChange = (field: keyof DashboardSettings, value: string) => {
    setDashboardFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setDashboardSaved(false);
  };

  const handleSaveDashboardSettings = async () => {
    try {
      setDashboardSaving(true);
      await updateDashboardSettings(dashboardFormData);
      setDashboardSaved(true);
      setTimeout(() => setDashboardSaved(false), 3000); // Clear success message after 3 seconds
    } catch (error) {
      console.error('Error saving dashboard settings:', error);
    } finally {
      setDashboardSaving(false);
    }
  };

  // Subsection management functions
  const addSubsection = (sectionId: string) => {
    const newSubsection: Subsection = {
      id: `subsection_${Date.now()}`,
      title: 'New Subsection',
      description: undefined, // Make description truly optional
      order: 0,
      questions: []
    };

    const section = currentTemplate.sections.find(s => s.id === sectionId);
    if (!section) return;

    const currentSubsections = section.subsections || [];
    newSubsection.order = currentSubsections.length;

    updateSection(sectionId, {
      subsections: [...currentSubsections, newSubsection]
    });
  };

  const updateSubsection = (sectionId: string, subsectionId: string, updates: Partial<Subsection>) => {
    const section = currentTemplate.sections.find(s => s.id === sectionId);
    if (!section || !section.subsections) return;

    const updatedSubsections = section.subsections.map(subsection =>
      subsection.id === subsectionId ? { ...subsection, ...updates } : subsection
    );

    updateSection(sectionId, { subsections: updatedSubsections });

    // Update currentSubsection if it's the one being edited
    if (currentSubsection?.id === subsectionId) {
      const updatedSubsection = updatedSubsections.find(sub => sub.id === subsectionId);
      if (updatedSubsection) {
        setCurrentSubsection(updatedSubsection);
      }
    }
  };
  
  // Update conditional rules for a subsection
  const updateSubsectionConditionalRules = (sectionId: string, subsectionId: string, conditionalRules: ConditionalRule[]) => {
    updateSubsection(sectionId, subsectionId, { conditionalRules });
  };

  const deleteSubsection = (sectionId: string, subsectionId: string) => {
    const section = currentTemplate.sections.find(s => s.id === sectionId);
    if (!section || !section.subsections) return;

    const updatedSubsections = section.subsections.filter(subsection => subsection.id !== subsectionId);
    updateSection(sectionId, { subsections: updatedSubsections });
  };

  const moveSubsection = (sectionId: string, dragIndex: number, hoverIndex: number) => {
    const section = currentTemplate.sections.find(s => s.id === sectionId);
    if (!section || !section.subsections) return;

    const draggedSubsection = section.subsections[dragIndex];
    const updatedSubsections = [...section.subsections];
    
    // Remove the dragged subsection
    updatedSubsections.splice(dragIndex, 1);
    // Insert it at the new position
    updatedSubsections.splice(hoverIndex, 0, draggedSubsection);

    // Update the order field for all subsections
    const reorderedSubsections = updatedSubsections.map((subsection, index) => ({
      ...subsection,
      order: index
    }));

    updateSection(sectionId, { subsections: reorderedSubsections });
  };

  const addQuestion = (sectionId: string, subsectionId?: string) => {
    const newQuestion: Question = {
      id: `question_${Date.now()}`,
      type: 'text',
      title: 'New Question',
      description: '',
      required: false,
      options: [],
      conditionalRules: [],
      metadata: {
        reusable: false,
        version: 1
      }
    };

    if (subsectionId) {
      // Add question to subsection
      const section = currentTemplate.sections.find(s => s.id === sectionId);
      if (!section || !section.subsections) return;

      const updatedSubsections = section.subsections.map(subsection =>
        subsection.id === subsectionId
          ? { ...subsection, questions: [...subsection.questions, newQuestion] }
          : subsection
      );

      updateSection(sectionId, { subsections: updatedSubsections });

      // Update currentSubsection if it's the one being edited
      if (currentSubsection?.id === subsectionId) {
        const updatedSubsection = updatedSubsections.find(sub => sub.id === subsectionId);
        if (updatedSubsection) {
          setCurrentSubsection(updatedSubsection);
        }
      }
    } else {
      // Add question directly to section
    updateSection(sectionId, {
      questions: [
        ...(currentTemplate.sections.find(s => s.id === sectionId)?.questions || []),
        newQuestion
      ]
    });
    }
    setCurrentQuestion(newQuestion);
    setCurrentQuestionSubsectionId(subsectionId || null);
  };

  const updateQuestion = (sectionId: string, questionId: string, updates: Partial<Question>, subsectionId?: string) => {
    const section = currentTemplate.sections.find(s => s.id === sectionId);
    if (!section) return;

    if (subsectionId) {
      // Update question in subsection
      if (!section.subsections) return;
      
      const updatedSubsections = section.subsections.map(subsection => {
        if (subsection.id === subsectionId) {
          const updatedQuestions = subsection.questions.map(question =>
            question.id === questionId ? { ...question, ...updates } : question
          );
          return { ...subsection, questions: updatedQuestions };
        }
        return subsection;
      });

      updateSection(sectionId, { subsections: updatedSubsections });

      // Update currentSubsection if it's the one being edited
      if (currentSubsection?.id === subsectionId) {
        const updatedSubsection = updatedSubsections.find(sub => sub.id === subsectionId);
        if (updatedSubsection) {
          setCurrentSubsection(updatedSubsection);
        }
      }
    } else {
      // Update question directly in section
    const updatedQuestions = section.questions.map(question =>
      question.id === questionId ? { ...question, ...updates } : question
    );

    updateSection(sectionId, { questions: updatedQuestions });
    }

    if (currentQuestion?.id === questionId) {
      setCurrentQuestion(prev => prev ? { ...prev, ...updates } : null);
    }
  };

  const deleteQuestion = (sectionId: string, questionId: string, subsectionId?: string) => {
    const section = currentTemplate.sections.find(s => s.id === sectionId);
    if (!section) return;

    if (subsectionId) {
      // Delete question from subsection
      if (!section.subsections) return;
      
      const updatedSubsections = section.subsections.map(subsection => {
        if (subsection.id === subsectionId) {
          const updatedQuestions = subsection.questions.filter(question => question.id !== questionId);
          return { ...subsection, questions: updatedQuestions };
        }
        return subsection;
      });

      updateSection(sectionId, { subsections: updatedSubsections });

      // Update currentSubsection if it's the one being edited
      if (currentSubsection?.id === subsectionId) {
        const updatedSubsection = updatedSubsections.find(sub => sub.id === subsectionId);
        if (updatedSubsection) {
          setCurrentSubsection(updatedSubsection);
        }
      }
    } else {
      // Delete question directly from section
    const updatedQuestions = section.questions.filter(question => question.id !== questionId);
    updateSection(sectionId, { questions: updatedQuestions });
    }

    if (currentQuestion?.id === questionId) {
      setCurrentQuestion(null);
      setCurrentQuestionSubsectionId(null);
    }
  };



  // Cross-container question movement
  const moveQuestionBetweenContainers = (
    draggedItem: { id: string; sectionId: string; subsectionId?: string; question: Question },
    targetSectionId: string,
    targetSubsectionId?: string
  ) => {
    console.log('🔧 moveQuestionBetweenContainers called with forced re-render approach');
    
    // Wrap the ENTIRE operation in flushSync to force immediate execution
    flushSync(() => {
      // Set drag operation flag to prevent auto-migration interference
      setIsDragOperation(true);
      
      // Force a complete re-render by updating a timestamp
      const timestamp = Date.now();
      console.log('⏰ Using timestamp for forced updates:', timestamp);
      
      const callId = `MOVE_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.log('🚨 About to call setCurrentTemplate - Call ID:', callId);
      
      setCurrentTemplate(prev => {
      console.log('📥 setCurrentTemplate executing - Call ID:', callId);
      
      const updatedSections = prev.sections.map(section => {
        // Same section move (source and target in same section)
        if (section.id === draggedItem.sectionId && section.id === targetSectionId && section.subsections) {
          console.log('🔄 Processing same-section move');
          
          const newSubsections = section.subsections.map(subsection => {
            // Remove from source
            if (subsection.id === draggedItem.subsectionId) {
              console.log('🗑️ Removing from source, before:', subsection.questions.length);
              console.log('🔍 Looking for question ID:', draggedItem.id);
              console.log('🔍 Source question IDs:', subsection.questions.map(q => q.id));
              const filtered = subsection.questions.filter(q => {
                const shouldKeep = q.id !== draggedItem.id;
                if (!shouldKeep) {
                  console.log('🎯 FOUND MATCH - Removing question:', q.id);
                }
                return shouldKeep;
              });
              console.log('🗑️ After filtering:', filtered.length);
              return {
                ...subsection,
                questions: filtered,
                _timestamp: timestamp
              };
            }
            // Add to target
            if (subsection.id === targetSubsectionId) {
              console.log('📝 Adding to target, before:', subsection.questions.length);
              
              // Check if question already exists in target (prevents duplicate adds)
              const questionExists = subsection.questions.some(q => q.id === draggedItem.id);
              
              // Only add if it doesn't already exist
              const newQuestions = questionExists 
                ? subsection.questions 
                : [...subsection.questions, draggedItem.question];
                
              console.log('📝 After adding:', newQuestions.length, questionExists ? '(already exists)' : '(added)');
              
              return {
                ...subsection,
                questions: newQuestions,
                _timestamp: timestamp
              };
            }
            return subsection;
          });
          
          console.log('✅ Created new section with updated subsections');
          return {
            ...section,
            subsections: newSubsections.map(sub => ({ ...sub, _timestamp: timestamp })), // Force all subsection objects to be new
            _timestamp: timestamp // Force React to see this as a new object
          };
        }
        
        // Cross-section moves (source section)
        if (section.id === draggedItem.sectionId) {
          console.log('🗑️ Processing cross-section source removal');
          if (draggedItem.subsectionId && section.subsections) {
            const newSubsections = section.subsections.map(subsection => 
              subsection.id === draggedItem.subsectionId
                ? { ...subsection, questions: subsection.questions.filter(q => q.id !== draggedItem.id), _timestamp: timestamp }
                : subsection
            );
            return { ...section, subsections: newSubsections, _timestamp: timestamp };
          } else {
            return { 
              ...section, 
              questions: section.questions.filter(q => q.id !== draggedItem.id),
              _timestamp: timestamp
            };
          }
        }
        
        // Cross-section moves (target section)
        if (section.id === targetSectionId) {
          console.log('📝 Processing cross-section target addition');
          if (targetSubsectionId && section.subsections) {
            const newSubsections = section.subsections.map(subsection => {
              if (subsection.id === targetSubsectionId) {
                // Check if question already exists in target (prevents duplicate adds)
                const questionExists = subsection.questions.some(q => q.id === draggedItem.id);
                
                // Only add if it doesn't already exist
                const newQuestions = questionExists 
                  ? subsection.questions 
                  : [...subsection.questions, draggedItem.question];
                
                console.log('📝 Cross-section add:', questionExists ? '(already exists)' : '(added)');
                
                return { 
                  ...subsection, 
                  questions: newQuestions, 
                  _timestamp: timestamp 
                };
              }
              return subsection;
            });
            return { ...section, subsections: newSubsections, _timestamp: timestamp };
          } else {
            // Check if question already exists in target section (prevents duplicate adds)
            const questionExists = section.questions.some(q => q.id === draggedItem.id);
            
            // Only add if it doesn't already exist
            const newQuestions = questionExists 
              ? section.questions 
              : [...section.questions, draggedItem.question];
            
            return { 
              ...section, 
              questions: newQuestions,
              _timestamp: timestamp
            };
          }
        }
        
        return section;
      });

      const newTemplate = {
        ...prev,
        sections: updatedSections,
        _lastUpdate: timestamp // Force template-level re-render
      };
      
      console.log('📤 Returning new template');
      console.log('📊 Old template sections count:', prev.sections.length);
      console.log('📊 New template sections count:', newTemplate.sections.length);
      
      // ⚡ CRITICAL: Use the NEW template for immediate updates, not the old currentTemplate
      console.log('⏳ Template update completed, applying immediate updates...');
      
      // Immediate cleanup and updates (since we're in flushSync)
      setIsDragOperation(false);
      
      // Update currentSubsection if editing the target (using NEW template)
      if (currentSubsection?.id === targetSubsectionId) {
        console.log('🔄 Updating currentSubsection immediately');
        const updatedSection = newTemplate.sections.find(s => s.id === targetSectionId);
        const updatedSubsection = updatedSection?.subsections?.find(s => s.id === targetSubsectionId);
        if (updatedSubsection) {
          setCurrentSubsection(updatedSubsection);
          console.log('✅ currentSubsection updated with NEW data');
        }
      }
      
      // Force complete re-render by updating currentSection if it's the affected section (using NEW template)
      if (currentSection?.id === targetSectionId) {
        const updatedSection = newTemplate.sections.find(s => s.id === targetSectionId);
        if (updatedSection) {
          console.log('🔄 Force updating currentSection with NEW template data');
          setCurrentSection({ ...updatedSection, _forceRender: Date.now() } as any);
        }
      }
      
      return newTemplate;
      });
    });
    
    console.log('🏁 Drag operation completed, all updates applied synchronously');
    console.log('✅ moveQuestionBetweenContainers completed with forced updates');
  };

  const addQuestionOption = (questionId: string, sectionId: string, subsectionId?: string) => {
    let question: Question | undefined;
    
    if (subsectionId) {
      // Look for question in subsection
      const section = currentTemplate.sections.find(s => s.id === sectionId);
      const subsection = section?.subsections?.find(sub => sub.id === subsectionId);
      question = subsection?.questions.find(q => q.id === questionId);
    } else {
      // Look for question in section
      question = currentTemplate.sections
        .find(s => s.id === sectionId)
        ?.questions.find(q => q.id === questionId);
    }

    if (!question) return;

    const newOption: QuestionOption = {
      id: `option_${Date.now()}`,
      label: 'New Option',
      value: `option_${Date.now()}`
    };

    updateQuestion(sectionId, questionId, {
      options: [...(question.options || []), newOption]
    }, subsectionId);
  };

  const updateQuestionOption = (
    questionId: string,
    sectionId: string,
    optionId: string,
    updates: Partial<QuestionOption>,
    subsectionId?: string
  ) => {
    let question: Question | undefined;
    
    if (subsectionId) {
      // Look for question in subsection
      const section = currentTemplate.sections.find(s => s.id === sectionId);
      const subsection = section?.subsections?.find(sub => sub.id === subsectionId);
      question = subsection?.questions.find(q => q.id === questionId);
    } else {
      // Look for question in section
      question = currentTemplate.sections
        .find(s => s.id === sectionId)
        ?.questions.find(q => q.id === questionId);
    }

    if (!question) return;

    const updatedOptions = (question.options || []).map(option =>
      option.id === optionId ? { ...option, ...updates } : option
    );

    updateQuestion(sectionId, questionId, { options: updatedOptions }, subsectionId);
  };

  const deleteQuestionOption = (questionId: string, sectionId: string, optionId: string, subsectionId?: string) => {
    let question: Question | undefined;
    
    if (subsectionId) {
      // Look for question in subsection
      const section = currentTemplate.sections.find(s => s.id === sectionId);
      const subsection = section?.subsections?.find(sub => sub.id === subsectionId);
      question = subsection?.questions.find(q => q.id === questionId);
    } else {
      // Look for question in section
      question = currentTemplate.sections
        .find(s => s.id === sectionId)
        ?.questions.find(q => q.id === questionId);
    }

    if (!question) return;

    const updatedOptions = (question.options || []).filter(option => option.id !== optionId);
    updateQuestion(sectionId, questionId, { options: updatedOptions }, subsectionId);
  };

  const moveQuestion = (sectionId: string, dragIndex: number, hoverIndex: number, subsectionId?: string) => {
    const section = currentTemplate.sections.find(s => s.id === sectionId);
    if (!section) return;

    if (subsectionId) {
      // Move question within subsection
      if (!section.subsections) return;
      
      const updatedSubsections = section.subsections.map(subsection => {
        if (subsection.id === subsectionId) {
          const draggedQuestion = subsection.questions[dragIndex];
          const updatedQuestions = [...subsection.questions];
          
          // Remove the dragged question
          updatedQuestions.splice(dragIndex, 1);
          // Insert it at the new position
          updatedQuestions.splice(hoverIndex, 0, draggedQuestion);

          return { ...subsection, questions: updatedQuestions };
        }
        return subsection;
      });

      updateSection(sectionId, { subsections: updatedSubsections });
      
      // Force update of currentSubsection if we're editing it
      if (currentSubsection?.id === subsectionId) {
        const updatedSubsection = updatedSubsections.find(sub => sub.id === subsectionId);
        if (updatedSubsection) {
          setCurrentSubsection(updatedSubsection);
        }
      }
    } else {
      // Move question within section
      const draggedQuestion = section.questions[dragIndex];
      const updatedQuestions = [...section.questions];
      
      // Remove the dragged question
      updatedQuestions.splice(dragIndex, 1);
      // Insert it at the new position
      updatedQuestions.splice(hoverIndex, 0, draggedQuestion);

      updateSection(sectionId, { questions: updatedQuestions });
    }
  };

  const moveSection = (dragIndex: number, hoverIndex: number) => {
    const draggedSection = currentTemplate.sections[dragIndex];
    const updatedSections = [...currentTemplate.sections];
    
    // Remove the dragged section
    updatedSections.splice(dragIndex, 1);
    // Insert it at the new position
    updatedSections.splice(hoverIndex, 0, draggedSection);

    // Update the order field for all sections
    const reorderedSections = updatedSections.map((section, index) => ({
      ...section,
      order: index + 1
    }));

    setCurrentTemplate(prev => ({
      ...prev,
      sections: reorderedSections
    }));
  };

  const handleSave = () => {
    // Remove backend-managed fields: id, version, isActive, and backend-managed metadata
    const { 
      id: _id, 
      version: _version, 
      isActive: _isActive, 
      ...templateWithoutBackendFields 
    } = currentTemplate;
    
    // Clean metadata to only include client-side fields
    const cleanMetadata = currentTemplate.metadata ? {
      category: currentTemplate.metadata.category,
      tags: currentTemplate.metadata.tags,
      estimatedTimeMinutes: currentTemplate.metadata.estimatedTimeMinutes
    } : undefined;
    
    const templateToSave = {
      ...templateWithoutBackendFields,
      metadata: cleanMetadata,
      // Remove _lastUpdate property used for rendering
      _lastUpdate: undefined,
      settings: {
        ...currentTemplate.settings,
        allowSaveProgress: currentTemplate.settings?.allowSaveProgress ?? true,
        allowBackNavigation: currentTemplate.settings?.allowBackNavigation ?? true,
        showProgressBar: currentTemplate.settings?.showProgressBar ?? true,
        requireAuthentication: currentTemplate.settings?.requireAuthentication ?? false
      },
      // Ensure sections have all required fields and remove internal properties
      sections: currentTemplate.sections.map(section => {
        // Remove _timestamp and other internal properties
        const { id: _sectionId, _timestamp, _forceRender, ...sectionWithoutId } = section as any;
        return {
          ...sectionWithoutId,
          id: section.id, // Keep section id as it's required
          questions: section.questions.map(question => {
            const { id: _questionId, ...questionWithoutId } = question;
            return {
              ...questionWithoutId,
              id: question.id, // Keep question id as it's required
              // Ensure question options have required fields
              options: question.options?.map(option => ({
                id: option.id, // Keep option id as it's required
                label: option.label,
                value: option.value
              }))
            };
          }),
          // Handle subsections if they exist - remove internal properties
          subsections: section.subsections?.map(subsection => {
            // Remove _timestamp and other internal properties
            const { id: _subsectionId, _timestamp, _forceRender, ...subsectionWithoutId } = subsection as any;
            return {
              ...subsectionWithoutId,
              id: subsection.id, // Keep subsection id as it's required
              questions: subsection.questions.map(question => {
                const { id: _questionId, ...questionWithoutId } = question;
                return {
                  ...questionWithoutId,
                  id: question.id, // Keep question id as it's required
                  // Ensure question options have required fields
                  options: question.options?.map(option => ({
                    id: option.id, // Keep option id as it's required
                    label: option.label,
                    value: option.value
                  }))
                };
              })
            };
          })
        };
      })
    };
    
    console.log('Saving template:', templateToSave);
    onSave(templateToSave);
  };

  const renderBasicSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Template Name *
        </label>
        <Input
          value={currentTemplate.name}
          onChange={(e) => setCurrentTemplate(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter template name"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Display Title *
        </label>
        <Input
          value={currentTemplate.title}
          onChange={(e) => setCurrentTemplate(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Enter questionnaire title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          value={currentTemplate.description || ''}
          onChange={(e) => setCurrentTemplate(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Enter questionnaire description"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category
        </label>
        <Input
          value={currentTemplate.metadata?.category || ''}
          onChange={(e) => setCurrentTemplate(prev => ({
            ...prev,
            metadata: { ...prev.metadata, category: e.target.value }
          }))}
          placeholder="e.g., Web Development, Marketing, Survey"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Estimated Time (minutes)
        </label>
        <Input
          type="number"
          value={currentTemplate.metadata?.estimatedTimeMinutes || 10}
          onChange={(e) => setCurrentTemplate(prev => ({
            ...prev,
            metadata: { ...prev.metadata, estimatedTimeMinutes: parseInt(e.target.value) }
          }))}
          min="1"
          max="120"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags (comma-separated)
        </label>
        <Input
          value={currentTemplate.metadata?.tags?.join(', ') || ''}
          onChange={(e) => setCurrentTemplate(prev => ({
            ...prev,
            metadata: {
              ...prev.metadata,
              tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
            }
          }))}
          placeholder="web, design, questionnaire"
        />
      </div>
    </div>
  );

  const renderSectionsList = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Sections</h3>
        <Button onClick={addSection}>Add Section</Button>
      </div>

      {currentTemplate.sections.map((section, index) => (
        <DraggableSectionCard
          key={section.id}
          section={section}
          index={index}
          onEdit={() => setCurrentSection(section)}
          onDelete={() => deleteSection(section.id)}
          onMove={moveSection}
        />
      ))}
    </div>
  );

  const renderSectionEditor = () => {
    if (!currentSection) {
      return (
        <div className="text-center py-8 text-gray-500">
          Select a section to edit or create a new one
        </div>
      );
    }

    const hasSubsections = currentSection.subsections && currentSection.subsections.length > 0;

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Edit Section: {currentSection.title}</h3>
          <Button variant="outline" onClick={() => setCurrentSection(null)}>
            Back to Sections
          </Button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section Title *
          </label>
          <Input
            value={currentSection.title}
            onChange={(e) => updateSection(currentSection.id, { title: e.target.value })}
            placeholder="Enter section title"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section Description (Optional)
            <span className="text-xs text-gray-500 ml-2">(Supports rich text and hyperlinks)</span>
          </label>
          <RichTextEditor
            value={currentSection.description || ''}
            onChange={(value) => updateSection(currentSection.id, { description: value })}
            placeholder="Enter an optional description for this section. You can format text and add hyperlinks."
            minimal={true}
            className="mb-2"
          />
        </div>

        {/* Conditional Rules Editor */}
        <div className="border-t pt-6">
          <h4 className="text-md font-medium mb-4">Conditional Display Rules</h4>
          <ConditionalRulesEditor 
            rules={currentSection.conditionalRules || []}
            onChange={(rules) => updateSectionConditionalRules(currentSection.id, rules)}
            availableQuestions={getAllAvailableQuestions()}
          />
          <div className="mt-2 text-sm text-gray-500">
            Use conditional rules to show or hide this section based on answers to other questions.
          </div>
        </div>

        {/* Structure Type Selection */}
        <div className="border-t pt-6">
          <h4 className="text-md font-medium mb-4">Section Structure</h4>

        </div>

        {/* Subsections */}
        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-medium">Subsections</h4>
            <div className="flex gap-2">
              <Button onClick={() => addSubsection(currentSection.id)} variant="outline">
                Add Subsection
              </Button>
            </div>
          </div>

          {hasSubsections ? (
            <div className="space-y-4">
              {currentSection.subsections!.map((subsection, index) => (
                <SubsectionCard
                  key={`${subsection.id}-${subsection.questions.length}-${(subsection as any)._timestamp || Date.now()}-${Math.random()}`}
                  subsection={subsection}
                  index={index}
                  sectionId={currentSection.id}
                  onEdit={(subsection) => setCurrentSubsection(subsection)}
                  onDelete={(subsectionId) => deleteSubsection(currentSection.id, subsectionId)}
                  onMoveSubsection={(dragIndex, hoverIndex) => moveSubsection(currentSection.id, dragIndex, hoverIndex)}
                  questionTypes={questionTypes}
                  moveQuestion={moveQuestion}
                  setCurrentQuestion={setCurrentQuestion}
                  setCurrentQuestionSubsectionId={setCurrentQuestionSubsectionId}
                  deleteQuestion={deleteQuestion}
                  moveQuestionBetweenContainers={moveQuestionBetweenContainers}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 border border-dashed border-gray-300 rounded-lg p-6">
                <p className="mb-4">No subsections yet. Click "Add Subsection" to create organized subsections.</p>
                <p className="text-sm text-gray-500">
                  💡 <strong>Tip:</strong> Subsections help organize your questions and provide a cleaner user experience.
                    </p>
                  </div>
            </div>
          )}
        </div>




      </div>
    );
  };

  const renderSubsectionEditor = () => {
    if (!currentSubsection || !currentSection) {
      return null;
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">Edit Subsection: {currentSubsection.title}</h3>
          <Button variant="outline" onClick={() => setCurrentSubsection(null)}>
            Back to Section
                    </Button>
                  </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subsection Title *
          </label>
          <Input
            value={currentSubsection.title}
            onChange={(e) => updateSubsection(currentSection.id, currentSubsection.id, { title: e.target.value })}
            placeholder="Enter subsection title"
          />
                </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subsection Description (Optional)
            <span className="text-xs text-gray-500 ml-2">(Supports rich text and hyperlinks)</span>
          </label>
          <RichTextEditor
            value={currentSubsection.description || ''}
            onChange={(value) => updateSubsection(currentSection.id, currentSubsection.id, { description: value })}
            placeholder="Enter an optional description for this subsection. You can format text and add hyperlinks."
            minimal={true}
            className="mb-2"
          />
        </div>
        
        {/* Conditional Rules Editor */}
        <div className="border-t pt-6">
          <h4 className="text-md font-medium mb-4">Conditional Display Rules</h4>
          <ConditionalRulesEditor 
            rules={currentSubsection.conditionalRules || []}
            onChange={(rules) => updateSubsectionConditionalRules(currentSection.id, currentSubsection.id, rules)}
            availableQuestions={getAllAvailableQuestions()}
          />
          <div className="mt-2 text-sm text-gray-500">
            Use conditional rules to show or hide this subsection based on answers to other questions.
          </div>
        </div>

        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-medium">Questions</h4>
            <Button onClick={() => addQuestion(currentSection.id, currentSubsection.id)}>Add Question</Button>
          </div>

          <div className="space-y-4">
            {currentSubsection.questions.map((question, index) => (
              <DraggableQuestionCard
                key={question.id}
                question={question}
                index={index}
                sectionId={currentSection.id}
                subsectionId={currentSubsection.id}
                questionTypes={questionTypes}
                onEdit={() => {
                  setCurrentQuestion(question);
                  setCurrentQuestionSubsectionId(currentSubsection.id);
                }}
                onDelete={() => deleteQuestion(currentSection.id, question.id, currentSubsection.id)}
                onMove={(dragIndex: number, hoverIndex: number) => moveQuestion(currentSection.id, dragIndex, hoverIndex, currentSubsection.id)}
              />
            ))}
            {currentSubsection.questions.length === 0 && (
              <div className="text-center py-8 text-gray-400 border border-dashed border-gray-300 rounded-lg">
                No questions yet. Click "Add Question" to add questions.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderQuestionEditor = () => {
    if (!currentQuestion || !currentSection) {
      return null;
    }

    const questionType = questionTypes.find(t => t.value === currentQuestion.type);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium">Edit Question</h3>
            <Button variant="outline" onClick={() => {
              setCurrentQuestion(null);
              setCurrentQuestionSubsectionId(null);
            }}>
              Close
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Type *
              </label>
              <select
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={currentQuestion.type}
                onChange={(e) => updateQuestion(
                  currentSection.id,
                  currentQuestion.id,
                  { type: e.target.value as Question['type'] },
                  currentQuestionSubsectionId || undefined
                )}
              >
                {questionTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Question Title *
              </label>
              <Input
                value={currentQuestion.title}
                onChange={(e) => updateQuestion(
                  currentSection.id,
                  currentQuestion.id,
                  { title: e.target.value },
                  currentQuestionSubsectionId || undefined
                )}
                placeholder="Enter question title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description/Help Text
                <span className="text-xs text-gray-500 ml-2">(Supports rich text and hyperlinks)</span>
              </label>
              <RichTextEditor
                value={currentQuestion.description || ''}
                onChange={(value) => updateQuestion(
                  currentSection.id,
                  currentQuestion.id,
                  { description: value },
                  currentQuestionSubsectionId || undefined
                )}
                placeholder="Optional help text for users. You can format text and add hyperlinks."
                minimal={true}
                className="mb-2"
              />
            </div>

            {/* Placeholder Text - only show for text-based input types */}
            {(['text', 'textarea', 'email', 'url', 'number'].includes(currentQuestion.type)) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Placeholder Text
                </label>
                <Input
                  value={currentQuestion.placeholder || ''}
                  onChange={(e) => updateQuestion(
                    currentSection.id,
                    currentQuestion.id,
                    { placeholder: e.target.value },
                    currentQuestionSubsectionId || undefined
                  )}
                  placeholder="Placeholder text for input fields"
                />
              </div>
            )}

            {/* File Accept Types - only show for file type questions */}
            {currentQuestion.type === 'file' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Accepted File Types
                  <span className="text-xs text-gray-500 ml-2">(e.g., .pdf,.doc,.jpg,.png or image/* or */*)</span>
                </label>
                <Input
                  value={currentQuestion.accept || ''}
                  onChange={(e) => updateQuestion(
                    currentSection.id,
                    currentQuestion.id,
                    { accept: e.target.value },
                    currentQuestionSubsectionId || undefined
                  )}
                  placeholder="*/*"
                />
              </div>
            )}

            {/* Range Configuration - only show for range and number type questions */}
            {(['range', 'number'].includes(currentQuestion.type)) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Value
                    <span className="text-xs text-gray-500 ml-2">(optional)</span>
                  </label>
                  <Input
                    type="number"
                    value={currentQuestion.min ?? ''}
                    onChange={(e) => updateQuestion(
                      currentSection.id,
                      currentQuestion.id,
                      { min: e.target.value ? parseFloat(e.target.value) : undefined },
                      currentQuestionSubsectionId || undefined
                    )}
                    placeholder="Min value"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Value
                    <span className="text-xs text-gray-500 ml-2">(optional)</span>
                  </label>
                  <Input
                    type="number"
                    value={currentQuestion.max ?? ''}
                    onChange={(e) => updateQuestion(
                      currentSection.id,
                      currentQuestion.id,
                      { max: e.target.value ? parseFloat(e.target.value) : undefined },
                      currentQuestionSubsectionId || undefined
                    )}
                    placeholder="Max value"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center">
              <Checkbox
                checked={currentQuestion.required || false}
                onCheckedChange={(checked) => updateQuestion(
                  currentSection.id,
                  currentQuestion.id,
                  { required: checked === true },
                  currentQuestionSubsectionId || undefined
                )}
              />
              <label className="ml-2 text-sm font-medium text-gray-700">
                Required question
              </label>
            </div>

            {questionType?.hasOptions && (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Options
                  </label>
                  <Button
                    size="sm"
                    onClick={() => addQuestionOption(currentQuestion.id, currentSection.id, currentQuestionSubsectionId || undefined)}
                  >
                    Add Option
                  </Button>
                </div>

                {currentQuestion.options?.map((option, index) => (
                  <div key={option.id} className="flex items-center space-x-2 mb-2">
                    <Input
                      value={option.label}
                      onChange={(e) => updateQuestionOption(
                        currentQuestion.id,
                        currentSection.id,
                        option.id,
                        { label: e.target.value },
                        currentQuestionSubsectionId || undefined
                      )}
                      placeholder="Option label"
                      className="flex-1"
                    />
                    <Input
                      value={option.value}
                      onChange={(e) => updateQuestionOption(
                        currentQuestion.id,
                        currentSection.id,
                        option.id,
                        { value: e.target.value },
                        currentQuestionSubsectionId || undefined
                      )}
                      placeholder="Option value"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteQuestionOption(currentQuestion.id, currentSection.id, option.id, currentQuestionSubsectionId || undefined)}
                      className="text-red-600"
                    >
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Conditional Rules Editor */}
          <div className="border-t pt-6 mt-6">
            <h4 className="text-md font-medium mb-4">Conditional Display Rules</h4>
            <ConditionalRulesEditor 
              rules={currentQuestion.conditionalRules || []}
              onChange={(rules) => updateQuestion(
                currentSection.id,
                currentQuestion.id,
                { conditionalRules: rules },
                currentQuestionSubsectionId || undefined
              )}
              availableQuestions={getAllAvailableQuestions()}
            />
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => {
              setCurrentQuestion(null);
              setCurrentQuestionSubsectionId(null);
            }}>
              Cancel
            </Button>
            <Button onClick={() => {
              setCurrentQuestion(null);
              setCurrentQuestionSubsectionId(null);
            }}>
              Save Question
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderSettings = () => (
    <div className="space-y-8">
      {/* Dashboard Settings Section */}
      <div className="space-y-6">
        <h3 className="text-lg font-medium">Dashboard Settings</h3>
        <p className="text-sm text-gray-600">
          Customize the welcome message and content displayed to users on the dashboard.
        </p>

        <div className="space-y-4">
          <div>
            <label htmlFor="welcomeTitle" className="block text-sm font-medium text-gray-700 mb-2">
              Welcome Title
            </label>
            <Input
              id="welcomeTitle"
              type="text"
              value={dashboardFormData.welcomeTitle}
              onChange={(e) => handleDashboardInputChange('welcomeTitle', e.target.value)}
              placeholder="Welcome to the Questionnaire"
            />
          </div>

          <div>
            <label htmlFor="welcomeDescription" className="block text-sm font-medium text-gray-700 mb-2">
              Welcome Description
            </label>
            <textarea
              id="welcomeDescription"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={dashboardFormData.welcomeDescription}
              onChange={(e) => handleDashboardInputChange('welcomeDescription', e.target.value)}
              placeholder="Please fill out the questionnaire to the best of your knowledge..."
            />
          </div>

          <div>
            <label htmlFor="startButtonText" className="block text-sm font-medium text-gray-700 mb-2">
              Start Button Text
            </label>
            <Input
              id="startButtonText"
              type="text"
              value={dashboardFormData.startButtonText}
              onChange={(e) => handleDashboardInputChange('startButtonText', e.target.value)}
              placeholder="Start New Questionnaire"
            />
          </div>

          <div className="flex gap-4 items-center">
            <Button
              onClick={handleSaveDashboardSettings}
              disabled={dashboardSaving}
              variant="outline"
            >
              {dashboardSaving ? 'Saving...' : 'Save Dashboard Settings'}
            </Button>
            {dashboardSaved && (
              <span className="text-green-600 text-sm">Dashboard settings saved successfully!</span>
            )}
          </div>
        </div>
      </div>

      {/* Questionnaire Settings Section */}
      <div className="space-y-6 border-t pt-6">
        <h3 className="text-lg font-medium">Questionnaire Settings</h3>

      <div className="space-y-4">
        <div className="flex items-center">
          <Checkbox
            checked={currentTemplate.settings.allowSaveProgress}
            onCheckedChange={(checked) => setCurrentTemplate(prev => ({
              ...prev,
              settings: { ...prev.settings, allowSaveProgress: checked === true }
            }))}
          />
          <label className="ml-2 text-sm font-medium text-gray-700">
            Allow users to save progress and continue later
          </label>
        </div>

        <div className="flex items-center">
          <Checkbox
            checked={currentTemplate.settings.allowBackNavigation}
            onCheckedChange={(checked) => setCurrentTemplate(prev => ({
              ...prev,
              settings: { ...prev.settings, allowBackNavigation: checked === true }
            }))}
          />
          <label className="ml-2 text-sm font-medium text-gray-700">
            Allow users to go back to previous sections
          </label>
        </div>

        <div className="flex items-center">
          <Checkbox
            checked={currentTemplate.settings.showProgressBar}
            onCheckedChange={(checked) => setCurrentTemplate(prev => ({
              ...prev,
              settings: { ...prev.settings, showProgressBar: checked === true }
            }))}
          />
          <label className="ml-2 text-sm font-medium text-gray-700">
            Show progress bar
          </label>
        </div>

        <div className="flex items-center">
          <Checkbox
            checked={currentTemplate.settings.requireAuthentication}
            onCheckedChange={(checked) => setCurrentTemplate(prev => ({
              ...prev,
              settings: { ...prev.settings, requireAuthentication: checked === true }
            }))}
          />
          <label className="ml-2 text-sm font-medium text-gray-700">
            Require user authentication
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Maximum Submissions per User
        </label>
        <Input
          type="number"
          value={currentTemplate.settings.maxSubmissions || ''}
          onChange={(e) => setCurrentTemplate(prev => ({
            ...prev,
            settings: {
              ...prev.settings,
              maxSubmissions: e.target.value ? parseInt(e.target.value) : undefined
            }
          }))}
          placeholder="Leave empty for unlimited"
          min="1"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Thank You Message
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          value={currentTemplate.settings.thankYouMessage || ''}
          onChange={(e) => setCurrentTemplate(prev => ({
            ...prev,
            settings: { ...prev.settings, thankYouMessage: e.target.value }
          }))}
          placeholder="Thank you for completing the questionnaire!"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Redirect URL (after completion)
        </label>
        <Input
          value={currentTemplate.settings.redirectUrl || ''}
          onChange={(e) => setCurrentTemplate(prev => ({
            ...prev,
            settings: { ...prev.settings, redirectUrl: e.target.value }
          }))}
          placeholder="https://example.com/thank-you"
        />
      </div>
      </div>
    </div>
  );

  const renderPreview = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Template Preview</h3>
      
      <Card>
        <CardHeader>
          <CardTitle>{currentTemplate.title || 'Untitled Questionnaire'}</CardTitle>
          {currentTemplate.description && (
            <p className="text-gray-600">{currentTemplate.description}</p>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {currentTemplate.sections.map((section, sectionIndex) => (
              <div key={section.id} className="border rounded-lg p-4">
                <h4 className="font-medium text-lg mb-2">{section.title}</h4>
                {section.description && (
                  <p className="text-gray-600 mb-4">{section.description}</p>
                )}
                
                <div className="space-y-3">
                  {section.questions.map((question, questionIndex) => (
                    <div key={question.id} className="border-l-2 border-gray-200 pl-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {question.title}
                        {question.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {question.description && (
                        <p className="text-sm text-gray-500 mb-2">{question.description}</p>
                      )}
                      
                      {/* Render different input types based on question type */}
                      {question.type === 'text' && (
                        <Input placeholder={question.placeholder} disabled />
                      )}
                      {question.type === 'textarea' && (
                        <textarea
                          className="w-full p-3 border border-gray-300 rounded-md"
                          rows={3}
                          placeholder={question.placeholder}
                          disabled
                        />
                      )}
                      {(question.type === 'radio' || question.type === 'select') && (
                        <div className="space-y-2">
                          {question.options?.map(option => (
                            <div key={option.id} className="flex items-center">
                              <input type="radio" disabled className="mr-2" />
                              <span className="text-sm">{option.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {question.type === 'checkbox' && (
                        <div className="space-y-2">
                          {question.options?.map(option => (
                            <div key={option.id} className="flex items-center">
                              <input type="checkbox" disabled className="mr-2" />
                              <span className="text-sm">{option.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading questionnaire...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Edit Current Questionnaire
        </h1>
        <div className="flex space-x-2">
          {/* Show migration button if there are any direct questions */}
          {currentTemplate.sections.some(section => section.questions.length > 0) && (
            <Button 
              variant="outline" 
              onClick={migrateDirectQuestionsToSubsections}
              className="text-orange-600 border-orange-600 hover:bg-orange-50"
            >
              🔄 Migrate to Subsections
            </Button>
          )}
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!currentTemplate.name || !currentTemplate.title}>
            Save Template
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'basic', label: 'Basic Info' },
            { id: 'sections', label: 'Sections & Questions' },
            { id: 'settings', label: 'Settings' },
            { id: 'email-notifications', label: 'Email Notifications' },
            { id: 'preview', label: 'Preview' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {activeTab === 'basic' && renderBasicSettings()}
        {activeTab === 'sections' && !currentSection && !currentSubsection && renderSectionsList()}
        {activeTab === 'sections' && currentSection && !currentSubsection && renderSectionEditor()}
        {activeTab === 'sections' && currentSubsection && renderSubsectionEditor()}
        {activeTab === 'settings' && renderSettings()}
        {activeTab === 'email-notifications' && <EmailNotificationSettings />}
        {activeTab === 'preview' && renderPreview()}
      </div>

      {/* Question Editor Modal */}
      {currentQuestion && renderQuestionEditor()}
    </div>
  );
};

export default QuestionnaireTemplateBuilder; 